// DocumentsTab — uploaded files list with download/preview + whitewash
// report link per doc (Phase 3D + 3H). Adds content-based replacement
// detection UI: "Replaces" / "Replaced by" badges, a "Compare" button per
// replacement pair, a "Mark as replacement for..." menu, and a "Detach"
// link (replacement-detect task).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileText,
  Download,
  Loader2,
  ShieldCheck,
  GitCompare,
  MoreVertical,
  Link2,
  Unlink,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Layers,
  UploadCloud,
  CheckCircle,
  AlertCircle,
  Type,
  FileDown,
  FileType,
  Ban,
  RotateCw,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import {
  listDocuments,
  assignReplacement,
  detachReplacement,
  uploadDocuments,
  type DocumentItem,
} from '../api/cases';
import { startCompare, getCompareJob, pollJob } from '../api/datawash';
import {
  purgeDocument,
  cancelIngest,
  reingestDocument,
  reextractDocument,
  getReextractDocumentStatus,
  getIngestStatus,
} from '../api/documents';
import {
  submitDocDrop,
  listCaseRequests,
  getRequest,
  type CaseRequest,
  type CaseRequestDocDropResponse,
  type CaseRequestDocReconstructResponse,
} from '../api/caseRequests';
import { useAuthStore } from '../stores/auth';
import type { ChatMessage } from '../api/chat';
import apiClient from '../api/client';
import WhitewashReportModal from './WhitewashReportModal';

// A row in the durable "Processing..." banner: one per in-flight doc_drop /
// doc_reconstruct case_request. State is reloaded from the ledger on mount
// so the banner survives tab + case switches + page reloads.
interface PendingDocRow {
  request_id: string;
  filename: string;
  kind: 'doc_drop' | 'doc_reconstruct';
  status: 'pending' | 'complete' | 'error';
  error?: string | null;
  // For doc_reconstruct complete rows: the reconstructed text, shown via a
  // "View" action so the user can read/copy it (the durable endpoint does
  // not open the Reconstruct popup; it returns the text inline).
  reconstructedText?: string;
  unknownTags?: string[];
  // True while the 2s "✓ uploaded" confirmation is showing before the row
  // is cleared from the banner.
  completedAt?: number;
}

function docFilenameOf(r: CaseRequest): string {
  const p = r.request_payload as { filename?: string } | null;
  return p?.filename || 'file';
}

interface DocumentsTabProps {
  caseId: string;
  refreshKey?: number;
  onDocumentsChanged?: () => void;
  // Fired when a comparison job (re)completes so the parent can switch to
  // the diff tab + announce it in the chat. originalDocId/revisionDocId let
  // the diff tab re-run without re-selecting.
  onCompareJob?: (
    jobId: string,
    summary?: string,
    originalFilename?: string,
    revisionFilenames?: string[],
    originalDocId?: string,
    revisionDocId?: string,
  ) => void;
  // Generic hook for pushing a system message into the chat from the
  // documents flow (used to announce auto-detected replacements +
  // reconstructed-text results from the durable doc-drop ledger).
  onExternalMessage?: (msg: ChatMessage) => void;
  // #B (operator 2026-11-04): parent registers a handler here that
  // DocumentsTab fills in with its seed+poll callback. ChatTab calls that
  // callback the instant it creates a doc_drop case_request so this tab's
  // 'Processing...' banner appears within ~1s of a chat -> documents tab
  // switch, without waiting for a ledger re-fetch.
  registerDocDropHandler?: (
    fn: ((row: { request_id: string; filename: string; kind: 'doc_drop' | 'doc_reconstruct' }) => void) | null,
  ) => void;
}

const POLL_INTERVAL = 2000;

export default function DocumentsTab({
  caseId,
  refreshKey,
  onDocumentsChanged,
  onExternalMessage,
  onCompareJob,
  registerDocDropHandler,
}: DocumentsTabProps) {
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportDoc, setReportDoc] = useState<DocumentItem | null>(null);
  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  // Per-doc loading flag for Re-extract (backgrounded job poll). Kept
  // separate from busyDocId so it does not collide with compare/assign/
  // detach which reset busyDocId in their own finally blocks.
  const [reextractLoadingDocId, setReextractLoadingDocId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // `submitting` is the brief moment between drop + submitDocDrop returning
  // the request_ids; the durable per-file status then lives in `pendingRows`.
  const [submitting, setSubmitting] = useState(false);
  // Durable "Processing N file(s)..." banner rows (one per in-flight
  // doc_drop / doc_reconstruct case_request).
  const [pendingRows, setPendingRows] = useState<PendingDocRow[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Refs so the poll callbacks always see the latest props without being
  // re-created (keeps poll loops alive across re-renders).
  const onDocumentsChangedRef = useRef(onDocumentsChanged);
  const onExternalMessageRef = useRef(onExternalMessage);
  useEffect(() => {
    onDocumentsChangedRef.current = onDocumentsChanged;
  }, [onDocumentsChanged]);
  useEffect(() => {
    onExternalMessageRef.current = onExternalMessage;
  }, [onExternalMessage]);
  // Set on unmount / case switch so in-flight poll loops exit cleanly.
  const pollAbort = useRef(false);

  // ---- case-request ledger: poll a pending doc_drop / doc_reconstruct row
  // until it lands, then update the banner. Backoff 2s -> 5s after 60s ->
  // 15s after 5min; cap 10min. Exits cleanly on case switch / unmount via
  // pollAbort. Mirrors ChatTab's pollLoop. ----
  const finalizeRequest = useCallback((r: CaseRequest) => {
    if (pollAbort.current) return;
    const filename = docFilenameOf(r);
    setPendingRows((prev) =>
      prev.map((row) =>
        row.request_id === r.id
          ? {
              ...row,
              status: r.status,
              kind: (r.kind as 'doc_drop' | 'doc_reconstruct') || row.kind,
              error: r.error,
              reconstructedText:
                r.kind === 'doc_reconstruct'
                  ? (r.response_payload as CaseRequestDocReconstructResponse | null)?.reconstructed_text
                  : undefined,
              unknownTags:
                r.kind === 'doc_reconstruct'
                  ? (r.response_payload as CaseRequestDocReconstructResponse | null)?.unknown_tags
                  : undefined,
              completedAt: r.status !== 'pending' ? Date.now() : row.completedAt,
            }
          : row,
      ),
    );
    if (r.status === 'complete') {
      if (r.kind === 'doc_drop') {
        // Refresh the document list so the newly-uploaded doc appears
        // in the Documents view (and any other refreshKey consumers).
        onDocumentsChangedRef.current?.();
        refreshDocs(false);
        // Mirror the chat-drop announcement so a doc dropped in the
        // Documents drop area also appears in the Chat view without a
        // manual refresh. The durable ledger row will re-render this as
        // a paired "Dropped <file>" + "Upload complete" message after a
        // reload; this live push covers the in-session case (ChatTab only
        // re-fetches the ledger on mount / case switch). Auto-replacement
        // notices still fire separately from refreshDocs for matched docs.
        const dropResp = r.response_payload as CaseRequestDocDropResponse | null;
        const count = dropResp?.doc_count || dropResp?.doc_ids?.length || 1;
        onExternalMessageRef.current?.({
          role: 'system',
          content: `**${filename}** uploaded. ${count} document(s) now available. Ask a question to start the review.`,
          result_link: 'documents',
        });
      } else if (r.kind === 'doc_reconstruct') {
        // The durable endpoint returns the reconstructed text inline (it
        // does NOT create a document). Surface it as a chat system message
        // so the user can read/copy it -- mirrors ChatTab's behavior.
        const resp = r.response_payload as CaseRequestDocReconstructResponse | null;
        const text = resp?.reconstructed_text || '(reconstruction yielded empty text)';
        onExternalMessageRef.current?.({
          role: 'system',
          content: `**${filename}** (reconstructed)\n\n${text}`,
          reconstructed: true,
          unknown_tags: resp?.unknown_tags,
          warnings: resp?.warnings,
          result_link: 'documents',
        });
      }
      // Show the "✓ uploaded / Reconstructed" confirmation for 2s, then
      // clear the row from the banner.
      setTimeout(() => {
        if (pollAbort.current) return;
        setPendingRows((prev) => prev.filter((row) => row.request_id !== r.id));
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollLoop = useCallback(
    async (requestId: string) => {
      const start = Date.now();
      let delay = POLL_INTERVAL;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((res) => setTimeout(res, delay));
        if (pollAbort.current) return;
        let r: CaseRequest;
        try {
          r = await getRequest(requestId);
        } catch {
          return; // transport error; leave the banner row in place
        }
        if (pollAbort.current) return;
        // Sync the row's kind + filename from the authoritative server row
        // (the server decides doc_drop vs doc_reconstruct; the placeholder
        // was 'doc_drop' until the first poll returns, so the pending label
        // flips from generic 'Processing...' to 'Uploading...' /
        // 'Reconstructing...' once we know).
        setPendingRows((prev) =>
          prev.map((row) =>
            row.request_id === requestId
              ? {
                  ...row,
                  kind: (r.kind as 'doc_drop' | 'doc_reconstruct') || row.kind,
                  filename: docFilenameOf(r) || row.filename,
                }
              : row,
          ),
        );
        if (r.status !== 'pending') {
          finalizeRequest(r);
          return;
        }
        const elapsed = Date.now() - start;
        if (elapsed >= 10 * 60 * 1000) {
          finalizeRequest({ ...r, status: 'error', error: 'request timed out (pending after 10min)' });
          return;
        }
        delay = elapsed > 5 * 60 * 1000 ? 15000 : elapsed > 60 * 1000 ? 5000 : POLL_INTERVAL;
      }
    },
    [finalizeRequest],
  );

  // Load any in-flight doc_drop / doc_reconstruct rows from the durable
  // ledger on mount (and on case switch) so the banner reappears with the
  // current per-file status after a tab / case switch or page reload.
  // Re-establishes poll loops for still-pending rows.
  useEffect(() => {
    pollAbort.current = false;
    let cancelled = false;
    (async () => {
      try {
        const ledger = await listCaseRequests(caseId, { include: 'pending,recent', limit: 50 });
        if (cancelled || pollAbort.current) return;
        const rows = (ledger.requests || []).filter(
          (r) =>
            (r.kind === 'doc_drop' || r.kind === 'doc_reconstruct') &&
            r.status === 'pending',
        );
        if (rows.length === 0) return;
        setPendingRows((prev) => {
          // Merge: keep any rows we already have (e.g. just-submitted), add
          // ledger rows we don't, preserving order by created_at.
          const seen = new Set(prev.map((p) => p.request_id));
          const merged = [...prev];
          for (const r of rows) {
            if (seen.has(r.id)) continue;
            seen.add(r.id);
            merged.push({
              request_id: r.id,
              filename: docFilenameOf(r),
              kind: r.kind as 'doc_drop' | 'doc_reconstruct',
              status: r.status,
              error: r.error,
              completedAt: r.status !== 'pending' ? Date.now() : undefined,
            });
          }
          return merged;
        });
        for (const r of rows) {
          // rows are already filtered to pending; (re)start their polls.
          pollLoop(r.id);
        }
      } catch {
        // Ledger endpoint unavailable -> banner just won't show prior
        // in-flight rows; the drop flow still works.
      }
    })();
    return () => {
      cancelled = true;
      pollAbort.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // #B (operator 2026-11-04): register a handler the parent (CaseDetailPane)
  // wires to ChatTab's onDocDropStarted. When ChatTab creates a doc_drop
  // case_request, it calls this with {request_id, filename, kind} so this
  // tab seeds a pendingRow + starts its own poll loop immediately. The
  // 'Processing N file(s)...' banner then shows within ~1s of a chat ->
  // documents tab switch, with no ledger re-fetch race. Cleared on unmount.
  useEffect(() => {
    if (!registerDocDropHandler) return;
    const handler = (row: { request_id: string; filename: string; kind: 'doc_drop' | 'doc_reconstruct' }) => {
      setPendingRows((prev) => {
        if (prev.some((p) => p.request_id === row.request_id)) return prev;
        return [
          ...prev,
          { request_id: row.request_id, filename: row.filename, kind: row.kind, status: 'pending', error: null },
        ];
      });
      pollLoop(row.request_id);
    };
    registerDocDropHandler(handler);
    return () => { registerDocDropHandler(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerDocDropHandler, pollLoop]);
  const [menuDocId, setMenuDocId] = useState<string | null>(null);
  // Purge confirmation modal target (the doc the user asked to delete).
  const [purgeDoc, setPurgeDoc] = useState<DocumentItem | null>(null);
  const [purging, setPurging] = useState(false);
  // UC-7.2: optional collapsible grouping of documents that are versions of
  // the same file (by base filename). The replacement-tracking badges in the
  // flat list already show version relationships; this toggle adds a
  // collapsible "cover_page.pdf (2 versions)" -> v1/v2 grouping view.
  const [groupByVersions, setGroupByVersions] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  // Hidden file input target for the "Compare with new version" flow
  // (Flow 2). When the user picks a file we upload it as a new document
  // then immediately start a comparison against the source doc.
  const compareNewRef = useRef<HTMLInputElement | null>(null);
  const compareNewTarget = useRef<DocumentItem | null>(null);

  function baseName(filename: string): string {
    const noExt = filename.replace(/\.[^.]+$/, '');
    return (
      noExt
        .replace(/[_-]?v\d+$/i, '')
        .replace(/[_-]?version\d*$/i, '')
        .replace(/\s*\(\d+\)\s*$/, '')
        .replace(/[_-]?copy$/i, '')
        .replace(/[_-]?\d{10,}$/i, '')
        .trim() || noExt
    );
  }
  function groupDocs(items: DocumentItem[]) {
    const map = new Map<string, DocumentItem[]>();
    for (const d of items) {
      const key = baseName(d.original_filename);
      const arr = map.get(key) || [];
      arr.push(d);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([key, gdocs]) => ({
      key,
      label: key,
      docs: gdocs.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    }));
  }
  const versionGroups = groupByVersions ? groupDocs(docs) : [];
  const { user } = useAuthStore();
  // Operators + admins (principal/compliance) may purge original files.
  const canPurge =
    !!user &&
    (user.role === 'operator' ||
      user.role === 'principal' ||
      user.role === 'compliance');
  // Track auto-detected replacements already announced to the chat so we
  // only fire the system message once per replacement (not on every refresh).
  const reportedAutoRef = useRef<Set<string>>(new Set());

  const refreshDocs = async (announce = true) => {
    try {
      const r = await listDocuments(caseId);
      const items = r.items || [];
      setDocs(items);
      // Fire a chat system message for any NEW auto-detected replacement
      // (suppressed on refresh-after-user-action via `announce`).
      if (announce && onExternalMessage) {
        for (const d of items) {
          if (
            d.replacement_detection === 'auto' &&
            d.replacement_of_id &&
            !reportedAutoRef.current.has(d.id)
          ) {
            reportedAutoRef.current.add(d.id);
            const oldName =
              d.replaces_document_filename ||
              items.find((x) => x.id === d.replacement_of_id)?.original_filename ||
              'a previous document';
            const match = d.replacement_similarity != null
              ? ` (${Math.round(d.replacement_similarity * 100)}% content match)`
              : '';
            onExternalMessage({
              role: 'system',
              content: `New document ${d.original_filename} appears to be a replacement for ${oldName}${match}. Comparison started automatically. [Not a replacement? Open the Documents tab to detach.]`,
              result_link: 'documents',
            });
          }
        }
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    refreshDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, refreshKey]);

  // oldDocId -> newDoc that replaces it (so the old doc can show a
  // "Replaced by" badge).
  const replacedByMap: Record<string, DocumentItem> = {};
  for (const d of docs) {
    if (d.replacement_of_id) replacedByMap[d.replacement_of_id] = d;
  }

  const download = async (doc: DocumentItem) => {
    if (doc.is_purged) return;
    try {
      const resp = await apiClient.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Download failed');
    }
  };

  const downloadText = async (doc: DocumentItem) => {
    if (doc.is_purged) return;
    try {
      const resp = await apiClient.get(`/documents/${doc.id}/text`);
      const text = resp.data?.text || resp.data?.extracted_text || '';
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stem = doc.original_filename.includes('.')
        ? doc.original_filename.split('.').slice(0, -1).join('.')
        : doc.original_filename;
      a.download = `${stem}_extracted.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Text extract download failed');
    }
  };

  const downloadStripped = async (doc: DocumentItem) => {
    if (doc.is_purged) return;
    if (!doc.has_pii_stripped_text) {
      setError('PII wash is still running for this document - stripped download will be available once it completes.');
      return;
    }
    try {
      const resp = await apiClient.get(`/documents/${doc.id}/download-stripped`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const stem = doc.original_filename.includes('.')
        ? doc.original_filename.split('.').slice(0, -1).join('.')
        : doc.original_filename;
      a.download = `${stem}_stripped.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Stripped text download failed');
    }
  };

  // Template-styled Word (.docx) exports from the Documents tab.
  // Both wrap the text in the shared _docx_template layout (title =
  // source filename, Calibri 11pt body, disclaimer footer).
  const _downloadDocxBlob = async (
    doc: DocumentItem,
    url: string,
    suffix: string,
    errMsg: string,
  ) => {
    if (doc.is_purged) return;
    try {
      const resp = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const stem = doc.original_filename.includes('.')
        ? doc.original_filename.split('.').slice(0, -1).join('.')
        : doc.original_filename;
      a.download = `${stem}${suffix}.docx`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e: any) {
      setError(e?.response?.data?.detail || errMsg);
    }
  };

  const downloadTextDocx = (doc: DocumentItem) =>
    _downloadDocxBlob(
      doc,
      `/documents/${doc.id}/download-text-docx`,
      '_FULLTEXT',
      'Full-text .docx download failed',
    );

  const downloadStrippedDocx = (doc: DocumentItem) => {
    if (!doc.has_pii_stripped_text) {
      setError('PII wash is still running for this document - stripped download will be available once it completes.');
      return;
    }
    return _downloadDocxBlob(
      doc,
      `/documents/${doc.id}/download-stripped-docx`,
      '_DATAWASHED',
      'Data-washed .docx download failed',
    );
  };

  // Start a comparison job between an old doc + a new (replacement) doc,
  // poll it to completion, then surface the result to the parent.
  const runComparePair = async (oldDoc: DocumentItem, newDoc: DocumentItem) => {
    setBusyDocId(newDoc.id);
    try {
      const start = await startCompare(caseId, oldDoc.id, [newDoc.id]);
      // Open the comparison tab immediately with the real doc names so the
      // tab label is correct from the moment it appears (the second call
      // below updates the summary once the job completes).
      onCompareJob?.(
        start.job_id,
        undefined,
        oldDoc.original_filename,
        [newDoc.original_filename],
        oldDoc.id,
        newDoc.id,
      );
      const job = await pollJob(() => getCompareJob(start.job_id), POLL_INTERVAL);
      const diffs = job.results?.differences || [];
      const origName =
        job.results?.original_filename || oldDoc.original_filename;
      const revNames =
        job.results?.revision_filenames || [newDoc.original_filename];
      const summary =
        job.results?.summary || `${diffs.length} difference(s) found.`;
      onCompareJob?.(start.job_id, summary, origName, revNames, oldDoc.id, newDoc.id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Comparison failed');
    } finally {
      setBusyDocId(null);
    }
  };

  // Flow 2: "Compare with new version" — the user picks a new file for an
  // existing document. We upload it as a new document in the case, then
  // immediately start a comparison between the existing doc (original) and
  // the freshly uploaded one (revision). Results surface in the chat + Diff
  // tab via onCompareJob + onExternalMessage.
  const onCompareNewVersion = (doc: DocumentItem) => {
    compareNewTarget.current = doc;
    compareNewRef.current?.click();
  };

  const onCompareNewFilePicked = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    const oldDoc = compareNewTarget.current;
    e.target.value = '';
    if (!files || files.length === 0 || !oldDoc) return;
    setBusyDocId(oldDoc.id);
    try {
      const result = await uploadDocuments(caseId, Array.from(files));
      const newDocs = result.items || [];
      await refreshDocs(false);
      onDocumentsChanged?.();
      if (newDocs.length === 0) {
        setError('Upload complete but no documents were registered.');
        return;
      }
      const newDoc = newDocs[0];
      onExternalMessage?.({
        role: 'system',
        content: `Comparing ${oldDoc.original_filename} with ${newDoc.original_filename}...`,
      });
      await runComparePair(oldDoc, newDoc);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Compare-with-new-version failed');
    } finally {
      setBusyDocId(null);
      compareNewTarget.current = null;
    }
  };

  const onDetach = async (doc: DocumentItem) => {
    setBusyDocId(doc.id);
    setMenuDocId(null);
    try {
      await detachReplacement(doc.id);
      await refreshDocs(false);
      onDocumentsChanged?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Detach failed');
    } finally {
      setBusyDocId(null);
    }
  };

  const onAssign = async (doc: DocumentItem, target: DocumentItem) => {
    setBusyDocId(doc.id);
    setMenuDocId(null);
    try {
      await assignReplacement(doc.id, target.id);
      await refreshDocs(false);
      onDocumentsChanged?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Assign failed');
    } finally {
      setBusyDocId(null);
    }
  };

  // Purge the original file bytes for a document (metadata row is kept
  // for the audit trail). Operator/admin only.
  const confirmPurge = async () => {
    if (!purgeDoc) return;
    setPurging(true);
    try {
      await purgeDocument(purgeDoc.id);
      await refreshDocs(false);
      onDocumentsChanged?.();
      setPurgeDoc(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  // Per-file ingest controls (ported from the orphan DocumentRow.tsx so
  // they render in the LIVE Documents tab). Backend endpoints + the
  // ../api/documents client functions already exist; only the live UI was
  // missing. Gates + confirm prompts + poll loop mirror DocumentRow.
  const handleCancelIngest = async (doc: DocumentItem) => {
    if (!window.confirm(
      `Cancel the in-flight AI analysis for "${doc.original_filename}"?\n` +
      `The document will be marked 'cancelled' (no further LLM calls). ` +
      `You can re-ingest it afterwards.`
    )) return;
    try {
      await cancelIngest(caseId, doc.id);
      await refreshDocs(false);
      onDocumentsChanged?.();
    } catch (e: any) {
      setError('Failed to cancel ingest (no in-flight job found, or already complete).');
    }
  };

  const handleReingest = async (doc: DocumentItem) => {
    const skipAi = window.confirm(
      `Re-ingest "${doc.original_filename}"?\n` +
      `OK = full re-ingest (re-extract text + re-run AI analysis; clears ` +
      `existing extracted facts for this case).\n` +
      `Cancel = re-extract TEXT ONLY (skip AI).`
    );
    setBusyDocId(doc.id);
    try {
      const resp = await reingestDocument(caseId, doc.id, { skip_ai: !skipAi });
      // FIX B: reingest returns 202 + a backgrounded job_id for long
      // docs (the AI pass runs async). The old code refreshed
      // IMMEDIATELY, so the doc stayed 'ingesting' and the case facts
      // never updated -- the UI looked frozen until a hard refresh.
      // Poll the ingest job to completion (mirrors handleReextract),
      // THEN refresh. For small docs / skip_ai the backend returns
      // status='completed' with job_id=null, so the poll is skipped and
      // behavior is unchanged.
      if (resp.job_id && resp.status !== 'completed') {
        for (let i = 0; i < 180; i++) {
          await new Promise((r) => setTimeout(r, 1500));
          let st;
          try {
            st = await getIngestStatus(caseId, doc.id, resp.job_id!);
          } catch {
            continue;
          }
          if (st.status === 'completed') break;
          if (st.status === 'failed')
            throw new Error(st.error || 'Re-ingest failed');
          if (st.status === 'cancelled')
            throw new Error('Re-ingest cancelled');
        }
      }
      await refreshDocs(false);
      onDocumentsChanged?.();
    } catch (e: any) {
      const detail =
        e?.message ||
        e?.response?.data?.detail ||
        'Failed to re-ingest document.';
      setError(detail);
      await refreshDocs(false);
      onDocumentsChanged?.();
    } finally {
      setBusyDocId(null);
    }
  };

  // Per-file re-extract: re-run extraction on this ONE intake (private)
  // doc + apply the FILL/PRESERVE/OVERWRITE/LEAVE field-merge policy.
  // Manual edits are preserved; AI/det values are overwritten; blanks are
  // filled. Backgrounded + cancelable. Shown for intake (non-public) docs.
  const handleReextract = async (doc: DocumentItem) => {
    const ok = window.confirm(
      `Re-extract this file?\n` +
      `Blank fields will be filled and AI-set fields will be overwritten ` +
      `with the latest extraction from this file. Fields you edited ` +
      `manually will be preserved.`
    );
    if (!ok) return;
    setReextractLoadingDocId(doc.id);
    try {
      const resp = await reextractDocument(caseId, doc.id);
      const poll = async (jid: string) => {
        for (let i = 0; i < 120; i++) {
          await new Promise((r) => setTimeout(r, 1500));
          let st;
          try {
            st = await getReextractDocumentStatus(caseId, doc.id, jid);
          } catch {
            continue;
          }
          if (st.status === 'completed') return st;
          if (st.status === 'failed')
            throw new Error(st.error || 'Re-extract failed');
          if (st.status === 'cancelled')
            throw new Error('Re-extract cancelled');
        }
        throw new Error('Re-extract timed out');
      };
      const final: any = await poll(resp.job_id);
      await refreshDocs(false);
      onDocumentsChanged?.();
      const filled =
        ((final?.summary as any)?.fields as any[])?.filter(
          (f) => f.decision === 'fill' || f.decision === 'overwrite'
        ).length ?? 0;
      alert(`Re-extract complete \u2014 ${filled} field(s) filled/updated.`);
    } catch (err: any) {
      const detail =
        err?.message ||
        err?.response?.data?.detail ||
        'Re-extract failed';
      console.error('Re-extract failed:', err);
      alert(detail);
    } finally {
      setReextractLoadingDocId(null);
    }
  };

  // Per-doc ingest-control buttons (Cancel ingest / Re-ingest / Re-extract).
  // Gates mirror DocumentRow.tsx. Rendered in all three per-doc paths.
  const renderIngestButtons = (doc: DocumentItem) => {
    const status = (doc.processing_status || 'pending').toUpperCase();
    const purged = !!doc.is_purged;
    const canCancel = !purged && (status === 'PENDING' || status === 'INGESTING');
    const canReingest =
      !purged &&
      (status === 'OK' ||
        status === 'ERRORED' ||
        status === 'CANCELLED' ||
        status === 'NOT_VERIFIABLE');
    // Re-extract: intake (non-public) docs only. Most ingested docs carry
    // a blank classification, so gate on "not public" rather than "== private".
    const canReextract = !purged && doc.classification !== 'public' && canReingest;
    const reextractLoading = reextractLoadingDocId === doc.id;
    return (
      <div className="flex items-center gap-1">
        {canCancel && (
          <button
            onClick={() => handleCancelIngest(doc)}
            className="btn-secondary !px-2 !py-1.5"
            title="Cancel ingest — cancel the in-flight AI analysis (marks the doc 'cancelled')"
            data-testid={`doc-cancel-ingest-${doc.id}`}
          >
            <Ban size={15} />
          </button>
        )}
        {canReingest && (
          <button
            onClick={() => handleReingest(doc)}
            disabled={busyDocId === doc.id}
            className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Re-ingest — re-run text extraction + AI analysis on the stored original"
            data-testid={`doc-reingest-${doc.id}`}
          >
            {busyDocId === doc.id ? (
              <RefreshCw size={15} className="animate-spin" />
            ) : (
              <RotateCw size={15} />
            )}
          </button>
        )}
        {canReextract && (
          <button
            onClick={() => handleReextract(doc)}
            disabled={reextractLoading}
            className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Re-extract — re-run extraction on this intake file. Blank fields are filled, AI-set fields are overwritten, manually-edited fields are preserved."
            data-testid={`doc-reextract-${doc.id}`}
          >
            {reextractLoading ? (
              <RefreshCw size={15} className="animate-spin" />
            ) : (
              <Sparkles size={15} />
            )}
          </button>
        )}
      </div>
    );
  };

  // ---- Drop zone upload handler (DURABLE case-request ledger) ----
  // Submits ALL dropped files via POST /cases/{id}/requests/doc-drop, which
  // creates one case_request row per file (kind doc_drop OR doc_reconstruct
  // depending on SERVER-SIDE PII-tag detection) + processes each in the
  // background. Returns request_ids immediately; per-file status is tracked
  // in `pendingRows` (the durable "Processing..." banner) + polled to
  // completion. This replaces the old client-side reconstructFile-per-file
  // + uploadDocuments batch flow, which lost its UI indicator the moment
  // the tab unmounted on a tab/case switch. See briefs/overviewtab-durable-drop.md.
  const handleFiles = async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const { request_ids } = await submitDocDrop(caseId, fileArr);
      if (pollAbort.current) return;
      // request_ids come back in the same order as the uploaded files.
      const newRows: PendingDocRow[] = [];
      for (let i = 0; i < request_ids.length; i++) {
        const rid = request_ids[i];
        const fname = fileArr[i]?.name || 'file';
        newRows.push({
          request_id: rid,
          filename: fname,
          // The server decides doc_drop vs doc_reconstruct; we won't know
          // the final kind until the first poll returns. Show a generic
          // "Processing" label until then.
          kind: 'doc_drop',
          status: 'pending',
        });
      }
      setPendingRows((prev) => [...prev, ...newRows]);
      for (const row of newRows) pollLoop(row.request_id);
    } catch (e: any) {
      setError(
        typeof e?.response?.data?.detail === 'string'
          ? e.response.data.detail
          : e?.message || 'Upload failed',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  // ---- Ingestion status rendering ----
  const renderStatus = (doc: DocumentItem) => {
    const status = (doc.processing_status || '').toLowerCase();
    if (status === 'pending') {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-slate-500"
          data-testid={`doc-status-${doc.id}`}
        >
          <Loader2 size={12} className="animate-spin" /> Extracting text...
        </span>
      );
    }
    if (status === 'ok') {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-green-600"
          data-testid={`doc-status-${doc.id}`}
        >
          <CheckCircle size={12} /> Ready
        </span>
      );
    }
    if (status === 'errored' || status === 'error') {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-red-600"
          data-testid={`doc-status-${doc.id}`}
          title={doc.processing_error || 'Extraction failed'}
        >
          <AlertCircle size={12} /> Error
        </span>
      );
    }
    if (status === 'not_verifiable') {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-amber-600"
          data-testid={`doc-status-${doc.id}`}
        >
          <AlertCircle size={12} /> Not verifiable
        </span>
      );
    }
    return null;
  };

  // Poll for ingestion status while any docs are pending, AND while any
  // doc is still awaiting its PII-wash background task (has_pii_stripped_text
  // not yet populated). The wash is decoupled from the ingest response
  // (operator 2026-10-26) so it can still be running after processing_status
  // flips to 'ok'. Bound the wash-pending poll to docs created within the
  // last 10 minutes so docs whose stripping mode is 'none' (no wash ever
  // produced -> has_pii_stripped_text stays false forever) do not poll
  // indefinitely.
  useEffect(() => {
    const hasPending = docs.some(
      (d) => d.processing_status === 'pending' || d.processing_status === 'ingesting',
    );
    const WASH_WINDOW_MS = 10 * 60 * 1000;
    const now = Date.now();
    const hasWashPending = docs.some(
      (d) =>
        !d.has_pii_stripped_text &&
        d.processing_status !== 'pending' &&
        d.created_at &&
        now - new Date(d.created_at).getTime() < WASH_WINDOW_MS,
    );
    if (!hasPending && !hasWashPending) return;
    const timer = setTimeout(() => refreshDocs(false), POLL_INTERVAL);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);

  const pct = (sim: number | null | undefined) =>
    sim == null ? '' : ` (${Math.round(sim * 100)}% similar)`;

  return (
    <div className="p-4 space-y-2" data-testid="documents-tab">
      {/* Hidden file input for the "Compare with new version" flow. */}
      <input
        ref={compareNewRef}
        type="file"
        className="hidden"
        onChange={onCompareNewFilePicked}
        data-testid="compare-new-file-input"
      />
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-primary-400 bg-primary-50'
            : 'border-slate-300 hover:border-slate-400 bg-slate-50'
        }`}
        data-testid="doc-drop-zone"
      >
        {submitting ? (
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Loader2 size={20} className="animate-spin" /> Submitting...
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-500">
            <UploadCloud size={28} className="text-slate-400" />
            <div className="text-sm font-medium">
              Drop documents here to upload
            </div>
            <div className="text-xs text-slate-400">or click to browse</div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
          data-testid="doc-file-input"
        />
      </div>

      {/* Durable "Processing N file(s)..." banner. One row per in-flight
          doc_drop / doc_reconstruct case_request. Survives tab + case
          switches + page reload because the rows are reloaded from the
          case_request ledger on mount. This is the operator's core ask:
          a durable indication that docs are processing in the background. */}
      {pendingRows.length > 0 && (
        <div
          className="rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-2"
          data-testid="doc-processing-banner"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-sky-800">
            <Loader2 size={14} className="animate-spin" />
            Processing {pendingRows.length} file
            {pendingRows.length === 1 ? '' : 's'}...
          </div>
          <ul className="space-y-1">
            {pendingRows.map((row) => (
              <li
                key={row.request_id}
                className="flex items-start gap-2 text-xs text-slate-700"
                data-testid={`doc-processing-row-${row.request_id}`}
              >
                {row.status === 'pending' && (
                  <>
                    <Loader2 size={13} className="mt-0.5 animate-spin text-sky-600 flex-shrink-0" />
                    <span className="truncate">
                      <span className="font-medium">{row.filename}</span>
                      {row.kind === 'doc_reconstruct'
                        ? ' \u2014 Reconstructing...'
                        : ' \u2014 Uploading...'}
                    </span>
                  </>
                )}
                {row.status === 'complete' && (
                  <>
                    <CheckCircle
                      size={13}
                      className="mt-0.5 text-green-600 flex-shrink-0"
                    />
                    <span className="truncate">
                      <span className="font-medium">{row.filename}</span>
                      {row.kind === 'doc_reconstruct'
                        ? ' \u2014 Reconstructed'
                        : ' \u2014 uploaded'}
                      {row.reconstructedText && (
                        <>
                          {' '}(
                          <a
                            className="text-sky-700 underline hover:text-sky-900"
                            onClick={() =>
                              onExternalMessageRef.current?.({
                                role: 'system',
                                content: `**${row.filename}** (reconstructed)\n\n${row.reconstructedText}`,
                                reconstructed: true,
                                unknown_tags: row.unknownTags,
                                result_link: 'documents',
                              })
                            }
                          >
                            view text
                          </a>
                          )
                        </>
                      )}
                    </span>
                  </>
                )}
                {row.status === 'error' && (
                  <>
                    <AlertCircle
                      size={13}
                      className="mt-0.5 text-red-600 flex-shrink-0"
                    />
                    <span className="truncate">
                      <span className="font-medium">{row.filename}</span>
                      {' \u2014 failed: '}
                      {row.error || 'unknown error'}{' '}
                      <button
                        className="text-slate-500 underline hover:text-slate-700"
                        onClick={() =>
                          setPendingRows((prev) =>
                            prev.filter((p) => p.request_id !== row.request_id),
                          )
                        }
                      >
                        dismiss
                      </button>
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading documents...
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600" data-testid="documents-error">
          {error}
        </div>
      )}
      {!loading && !error && docs.length === 0 && (
        <div className="text-center text-slate-400 py-12 text-sm">
          No documents uploaded yet.
        </div>
      )}
      {!loading && !error && docs.length > 0 && (
        <div className="flex items-center justify-end">
          <button
            onClick={() => setGroupByVersions((v) => !v)}
            className="btn-secondary !px-2 !py-1 text-xs flex items-center gap-1.5"
            title="Toggle version grouping by filename"
            data-testid="docs-group-toggle"
          >
            <Layers size={13} /> {groupByVersions ? 'Ungroup' : 'Group versions'}
          </button>
        </div>
      )}
      {!loading && groupByVersions && (
        <div className="space-y-2">
          {versionGroups.map((grp) => {
            const isGroup = grp.docs.length > 1;
            const collapsed = !!collapsedGroups[grp.key];
            if (!isGroup) {
              const doc = grp.docs[0];
              return (
                <div key={grp.key} className="card p-3 flex items-center gap-3" data-testid={`doc-row-${doc.id}`}>
                  <FileText size={20} className="text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</div>
                    <div className="text-xs text-slate-500">{new Date(doc.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => download(doc)} className="btn-secondary !px-2 !py-1.5" title="Download" data-testid={`doc-download-${doc.id}`}><Download size={15} /></button>
                    <button onClick={() => downloadText(doc)} className="btn-secondary !px-2 !py-1.5" title="Download extracted text" data-testid={`doc-text-${doc.id}`}><Type size={15} /></button>
                    <button onClick={() => downloadTextDocx(doc)} className="btn-secondary !px-2 !py-1.5" title="Download full text as formatted Word (.docx)" data-testid={`doc-text-docx-${doc.id}`}><FileType size={15} /></button>
                  </div>
                  {renderIngestButtons(doc)}
                  <div className="w-px h-5 bg-slate-200 mx-1" />
                  <div className="flex items-center gap-1">
                    <button onClick={() => downloadStripped(doc)} disabled={!doc.has_pii_stripped_text} className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title={doc.has_pii_stripped_text ? 'Download PII-stripped text' : 'PII wash in progress - available once it completes'} data-testid={`doc-stripped-${doc.id}`}><ShieldCheck size={15} /></button>
                    <button onClick={() => downloadStrippedDocx(doc)} disabled={!doc.has_pii_stripped_text} className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title={doc.has_pii_stripped_text ? 'Download PII-stripped (data-washed) as formatted Word (.docx)' : 'PII wash in progress - available once it completes'} data-testid={`doc-stripped-docx-${doc.id}`}><ShieldCheck size={15} /></button>
                    <button onClick={() => setReportDoc(doc)} disabled={!doc.has_pii_stripped_text} className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title={doc.has_pii_stripped_text ? 'Generate a polished PII audit report via the agent pipeline (stripped data -> Claude -> reconstructed .docx)' : 'PII wash in progress - available once it completes'} data-testid={`doc-whitewash-${doc.id}`}><ShieldCheck size={15} /> PII report</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={grp.key} className="card p-0 overflow-hidden" data-testid={`doc-group-${grp.key}`}>
                <button
                  onClick={() => setCollapsedGroups((prev) => ({ ...prev, [grp.key]: !collapsed }))}
                  className="w-full flex items-center gap-2 p-3 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`doc-group-toggle-${grp.key}`}
                >
                  {collapsed ? <ChevronRight size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  <FileText size={18} className="text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {grp.label} <span className="text-xs text-slate-400">({grp.docs.length} versions)</span>
                    </div>
                  </div>
                </button>
                {!collapsed && (
                  <div className="divide-y divide-slate-100 border-t border-slate-100">
                    {grp.docs.map((doc, vi) => (
                      <div key={doc.id} className="flex items-center gap-3 p-3" data-testid={`doc-row-${doc.id}`}>
                        <div className="w-6 text-xs font-mono text-slate-400 text-center flex-shrink-0">v{vi + 1}</div>
                        <FileText size={18} className="text-slate-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</div>
                          <div className="text-xs text-slate-500">{new Date(doc.created_at).toLocaleString()}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => download(doc)} className="btn-secondary !px-2 !py-1.5" title="Download" data-testid={`doc-download-${doc.id}`}><Download size={15} /></button>
                          <button onClick={() => downloadText(doc)} className="btn-secondary !px-2 !py-1.5" title="Download extracted text" data-testid={`doc-text-${doc.id}`}><Type size={15} /></button>
                          <button onClick={() => downloadTextDocx(doc)} className="btn-secondary !px-2 !py-1.5" title="Download full text as formatted Word (.docx)" data-testid={`doc-text-docx-${doc.id}`}><FileType size={15} /></button>
                        </div>
                        {renderIngestButtons(doc)}
                        <div className="w-px h-5 bg-slate-200 mx-1" />
                        <div className="flex items-center gap-1">
                          <button onClick={() => downloadStripped(doc)} disabled={!doc.has_pii_stripped_text} className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title={doc.has_pii_stripped_text ? 'Download PII-stripped text' : 'PII wash in progress - available once it completes'} data-testid={`doc-stripped-${doc.id}`}><ShieldCheck size={15} /></button>
                          <button onClick={() => downloadStrippedDocx(doc)} disabled={!doc.has_pii_stripped_text} className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title={doc.has_pii_stripped_text ? 'Download PII-stripped (data-washed) as formatted Word (.docx)' : 'PII wash in progress - available once it completes'} data-testid={`doc-stripped-docx-${doc.id}`}><ShieldCheck size={15} /></button>
                          <button onClick={() => setReportDoc(doc)} disabled={!doc.has_pii_stripped_text} className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title={doc.has_pii_stripped_text ? 'Generate a polished PII audit report via the agent pipeline (stripped data -> Claude -> reconstructed .docx)' : 'PII wash in progress - available once it completes'} data-testid={`doc-whitewash-${doc.id}`}><ShieldCheck size={15} /> PII report</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {!loading && !groupByVersions &&
        docs.map((doc) => {
          const isReplacement = !!doc.replacement_of_id; // this doc is the NEW one
          const replacedBy = replacedByMap[doc.id]; // this doc is the OLD one
          const oldFilename =
            doc.replaces_document_filename ||
            docs.find((x) => x.id === doc.replacement_of_id)?.original_filename ||
            '';
          // Candidates for "Mark as replacement for": other docs in the case
          // that are not already a replacement themselves and not this doc.
          const assignTargets = docs.filter(
            (x) => x.id !== doc.id && !x.replacement_of_id,
          );
          return (
            <div
              key={doc.id}
              className="card p-3 flex items-center gap-3"
              data-testid={`doc-row-${doc.id}`}
            >
              <FileText size={20} className="text-slate-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                  {doc.original_filename}
                  {doc.is_purged && (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 ring-1 ring-slate-200"
                      data-testid={`doc-purged-badge-${doc.id}`}
                    >
                      <AlertTriangle size={11} /> Purged
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {doc.document_type || doc.classification || 'Document'}
                  {renderStatus(doc)}
                </div>
                {/* Replacement badges */}
                {isReplacement && (
                  <div
                    className="mt-1 flex flex-wrap items-center gap-2 text-xs"
                    data-testid={`doc-replaces-badge-${doc.id}`}
                  >
                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700 ring-1 ring-amber-200">
                      <Link2 size={12} />
                      Replaces: {oldFilename}
                      {doc.replacement_detection === 'auto' &&
                        pct(doc.replacement_similarity)}
                    </span>
                    <button
                      onClick={() =>
                        runComparePair(
                          docs.find((x) => x.id === doc.replacement_of_id)!,
                          doc,
                        )
                      }
                      disabled={busyDocId === doc.id}
                      className="btn-secondary !px-2 !py-0.5 !text-xs"
                      data-testid={`doc-replaces-compare-${doc.id}`}
                    >
                      {busyDocId === doc.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <GitCompare size={12} />
                      )}{' '}
                      Compare
                    </button>
                    {doc.replacement_detection === 'auto' && (
                      <button
                        onClick={() => onDetach(doc)}
                        disabled={busyDocId === doc.id}
                        className="text-slate-400 underline hover:text-slate-600"
                        data-testid={`doc-replaces-detach-${doc.id}`}
                      >
                        Not a replacement?
                      </button>
                    )}
                    {doc.replacement_detection === 'manual' && (
                      <button
                        onClick={() => onDetach(doc)}
                        disabled={busyDocId === doc.id}
                        className="inline-flex items-center gap-1 text-slate-400 underline hover:text-slate-600"
                        data-testid={`doc-replaces-detach-manual-${doc.id}`}
                      >
                        <Unlink size={12} /> Detach
                      </button>
                    )}
                  </div>
                )}
                {replacedBy && !isReplacement && (
                  <div
                    className="mt-1 flex flex-wrap items-center gap-2 text-xs"
                    data-testid={`doc-replacedby-badge-${doc.id}`}
                  >
                    <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 ring-1 ring-sky-200">
                      Replaced by: {replacedBy.original_filename}
                    </span>
                    <button
                      onClick={() => runComparePair(doc, replacedBy)}
                      disabled={busyDocId === doc.id}
                      className="btn-secondary !px-2 !py-0.5 !text-xs"
                      data-testid={`doc-replacedby-compare-${doc.id}`}
                    >
                      {busyDocId === doc.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <GitCompare size={12} />
                      )}{' '}
                      Compare
                    </button>
                  </div>
                )}
              </div>
              {/* Flow 2: "Compare with new version" — upload a new file and
                  diff it against this document. */}
              <button
                onClick={() => onCompareNewVersion(doc)}
                disabled={busyDocId === doc.id || !!doc.is_purged}
                className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Upload a new version and compare"
                data-testid={`doc-compare-new-${doc.id}`}
              >
                {busyDocId === doc.id ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <GitCompare size={15} />
                )}
              </button>
              {/* "Mark as replacement for..." menu (only when not already a
                  replacement and there is at least one candidate target). */}
              {!isReplacement && assignTargets.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() =>
                      setMenuDocId(menuDocId === doc.id ? null : doc.id)
                    }
                    disabled={busyDocId === doc.id}
                    className="btn-secondary !px-2 !py-1.5"
                    title="Mark as replacement for..."
                    data-testid={`doc-menu-${doc.id}`}
                  >
                    <MoreVertical size={15} />
                  </button>
                  {menuDocId === doc.id && (
                    <>
                      {/* click-outside backdrop */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setMenuDocId(null)}
                      />
                      <div
                        className="absolute right-0 z-20 mt-1 w-64 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
                        data-testid={`doc-menu-dropdown-${doc.id}`}
                      >
                        <div className="px-3 py-1 text-xs font-medium text-slate-500">
                          Mark as replacement for...
                        </div>
                        {assignTargets.map((target) => (
                          <button
                            key={target.id}
                            onClick={() => onAssign(doc, target)}
                            className="block w-full truncate px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                            data-testid={`doc-assign-target-${doc.id}-${target.id}`}
                          >
                            {target.original_filename}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => download(doc)}
                  disabled={!!doc.is_purged}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={doc.is_purged ? 'Original purged — download disabled' : 'Download original file'}
                  data-testid={`doc-download-${doc.id}`}
                >
                  <Download size={15} />
                </button>
                <button
                  onClick={() => downloadText(doc)}
                  disabled={!!doc.is_purged}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Download extracted text"
                  data-testid={`doc-text-${doc.id}`}
                >
                  <Type size={15} />
                </button>
                <button
                  onClick={() => downloadTextDocx(doc)}
                  disabled={!!doc.is_purged}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Download full text as formatted Word (.docx)"
                  data-testid={`doc-text-docx-${doc.id}`}
                >
                  <FileType size={15} />
                </button>
              </div>
              {renderIngestButtons(doc)}
              <div className="w-px h-5 bg-slate-200 mx-1" />
              <div className="flex items-center gap-1">
                <button
                  onClick={() => downloadStripped(doc)}
                  disabled={!!doc.is_purged || !doc.has_pii_stripped_text}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={doc.has_pii_stripped_text ? 'Download PII-stripped text' : 'PII wash in progress - available once it completes'}
                  data-testid={`doc-stripped-${doc.id}`}
                >
                  <FileDown size={15} />
                </button>
                <button
                  onClick={() => downloadStrippedDocx(doc)}
                  disabled={!!doc.is_purged || !doc.has_pii_stripped_text}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={doc.has_pii_stripped_text ? 'Download PII-stripped (data-washed) as formatted Word (.docx)' : 'PII wash in progress - available once it completes'}
                  data-testid={`doc-stripped-docx-${doc.id}`}
                >
                  <ShieldCheck size={15} />
                </button>
                <button
                  onClick={() => setReportDoc(doc)}
                  disabled={!doc.has_pii_stripped_text}
                  className="btn-secondary !px-2 !py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={doc.has_pii_stripped_text ? 'Generate a polished PII audit report via the agent pipeline (stripped data -> Claude -> reconstructed .docx)' : 'PII wash in progress - available once it completes'}
                  data-testid={`doc-whitewash-${doc.id}`}
                >
                  <ShieldCheck size={15} /> PII report
                </button>
              </div>
              {canPurge && (
                <button
                  onClick={() => setPurgeDoc(doc)}
                  disabled={!!doc.is_purged || busyDocId === doc.id}
                  className="btn-secondary !px-2 !py-1.5 !text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={doc.is_purged ? 'Original already purged' : 'Delete (purge original file)'}
                  data-testid={`doc-purge-${doc.id}`}
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          );
        })}
      {reportDoc && (
        <WhitewashReportModal
          documentId={reportDoc.id}
          documentName={reportDoc.original_filename}
          onClose={() => setReportDoc(null)}
        />
      )}
      {purgeDoc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="purge-confirm-modal"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-red-50 p-2 text-red-600">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900">
                  Permanently remove this document?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  The file <span className="font-medium">{purgeDoc.original_filename}</span> will
                  be purged but the metadata row is kept for the audit trail. This
                  cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setPurgeDoc(null)}
                disabled={purging}
                className="btn-secondary"
                data-testid="purge-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurge}
                disabled={purging}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="purge-confirm"
              >
                {purging ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Trash2 size={15} />
                )}
                Purge original
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}